import React, { useState, useEffect } from 'react';
import { makeStyles, Theme, createStyles } from '@mui/styles';
import { Button, ButtonGroup, Typography, Card, CardHeader, CardContent,  CardActionArea, Table, TableContainer, TableBody, TableHead, TableRow, TableCell } from '@mui/material';
import { Field } from '~common/components/form-def-field';
import EnhancedTable from '~common/components/query-fulfillment/query-results-summary-table';


const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      margin: theme.spacing(1),
    },
    cardHeader: {
      width: "calc(innerWidth - 30)",
      display: "flex",
      justifyContent: "space-between",
      padding: "5px 0px 5px 15px",
    },
    cardBody: {
      display: "flex",
      height: "calc(100% - " + theme.spacing(4) + ")",
    },
    cardHeaderTitle: {
    },
    cardHeaderControls: {
      fontSize: "0.75em",
      color: "#888888",
      opacity: 0.9,
    },
    cardHeaderButton: {
      color: "#888888",
      opacity: 0.9,
      cursor: "arrow",
    },
    activeModeControl: {
      color: theme.palette.primary.main, 
      opacity: 0.9,
      cursor: "pointer",
    },
    inactiveModeControl: {
      color: "#888888",
      opacity: 0.9,
      cursor: "arrow",
    },
    cardContent: {
      flex: 1,
      paddingTop: 5,
      paddingBottom: "5px !important",
      textAlign: "center",
      display: "flex",
    },
    summaryArea: {
      paddingTop: 20,
      position: "relative",
      minHeight: "100px",
      flex: 1,
    },
    summaryAreaEmpty: {
      paddingTop: 60,
      position: "relative",
      minHeight: "100px",
      flex: 1,
    },
    summaryAreaSelectable: {
      position: "relative",
      minHeight: "100px",
      cursor: "pointer",
      flex: 1,
    },
    summaryTableArea: {
      padding: 0,
      overflow: "scroll",
    },
    summaryField: {

    },
    facetTitle: {
      fontSize: "1.2em",
      marginBottom: theme.spacing(1),
    },
    facetValue: {
      top: "40px",
      fontSize: "1.6em",
      fontWeight: 600,
      userSelect: 'auto',
    },
    title: {
      fontSize: "1.2em",
    },
    controls: {
      width: "100%",
      position: "absolute",
      bottom: 0,
      display: "flex",
      justifyContent: "space-between",
      fontSize: "0.8em",
      marginBottom: theme.spacing(1),
    },
    nextActive: {
      cursor: "pointer",
      color: "#888888",
    },
    nextInactive: {
      cursor: "not-allowed",
      color: "#cccccc",
    },
    prevActive: {
      cursor: "pointer",
      color: "#888888",
    },
    prevInactive: {
      cursor: "not-allowed",
      color: "#cccccc",
    },
    pageIndicator: {
      color: "#cccccc",
      fontSize: "0.5em",
      padding: "0 2px",
    },
    pageIndicatorCurrent: {
      //color: "#888888",
      color: theme.palette.primary.main,
      fontSize: "0.5em",
      padding: "0 2px",
    },
    tableHeaderColumn: {
      verticalAlign: "bottom",

    }

  }),
);

interface SummaryCardProps {
  data: any;
  onClick?: Function 
  selected?: boolean
  suppressTitlem?: boolean
}

export const SummaryCard: React.FC<SummaryCardProps> = ({ data, onClick, selected, classes, suppressTitle }) => {
  // This allows us to override the default styles by passing in a classes object
  var classesLocal = useStyles();
  if(classes) {
    classesLocal = Object.assign({}, classesLocal, classes);
  }
  classes = classesLocal;


  const [facetIdx, setFacetIdx] = useState(0);
  // View Modes: card, table 
  const [viewMode, setViewMode] = useState("card");

  const handleNext = (evt:any) => {
    evt.preventDefault();
    evt.stopPropagation();
    if (facetIdx < data.facets.length - 1) {
      setFacetIdx(facetIdx + 1);
    } else {
      setFacetIdx(0);
    }
  };

  const handlePrev = (evt:any) => {
    evt.preventDefault();
    evt.stopPropagation();
    if (facetIdx > 0) {
      setFacetIdx(facetIdx - 1);
    } else {
      setFacetIdx(data.facets.length - 1);
    }
  };


  const handleClick = (evt:any) => {
    if (onClick) {
      onClick(evt, data);
    }
  };

  const setMode = (mode) => (evt:any) => {
    setViewMode(mode);
  };

  var cardContent: any = null;


  const currentFacet = data.facets[facetIdx]
  if(!currentFacet) {
    // This can happen if no results are returned
    console.warn("No facet found for index: " + facetIdx);
    cardContent = (
      <div className={classes.summaryAreaEmpty}>
        <div>
          <div className={classes.facetValue}>
            --
          </div>
        </div>
      </div>
    )          
  } else {

    if(!data || !data.facets || data.facets.length == 0) {
      console.error("SummaryCard: data is not valid for summary: ", data);
      return null;
    }

    let controls: any = null;
    var indicators: Array<any> = [];

    for(var i=0; i<data.facets.length; i++) {
      var indicatorClass = classes.pageIndicator;
      if (i == facetIdx) {
        indicatorClass = classes.pageIndicatorCurrent;
      }
      indicators.push(
          <i key={"facet_indicator" + (i+1)} className={"fa fa-circle " + indicatorClass} />
      )
    }

    if (data.facets.length > 1) {
      controls = (
        <div className={classes.controls}>
          <i className={"fa fa-chevron-left " + classes.prevActive} onClick={handlePrev} />
          <div>
            {indicators}
          </div>
          <i className={"fa fa-chevron-right " + classes.prevActive} onClick={handleNext} />
        </div>
      )
    } 


    // In this area, we need to create a local field definition and an object to represent the section data
    var localField = {
      mode: "Display",
      // Since we are only ever displaying one value, we can use the summary card's key as the section key
      key: data.key,
      title: "",
      type: currentFacet.type,
    }

    var secObj = {}
    secObj[data.key] = currentFacet.value

    // We are going to use the selectable to determine the class for the summary area 

    let summaryAreaClass = classes.summaryArea;
    var summaryAreaHandler:any = undefined
    // If we have an onClick handler, then we can assume that the card is selectable
    if (onClick) {
      summaryAreaClass += " " + classes.summaryAreaSelectable;
      summaryAreaHandler = handleClick;
    }

    var tableModeClass = "fa fa-th-list "
    var cardModeClass = "fa fa-th-large "
    switch (viewMode) {
      case "table":
        tableModeClass += classes.activeModeControl
        break;
      case "card":
        cardModeClass += classes.activeModeControl
        break;
    }

    // Only if we have more that one facet do we want to allow the user to switch to table mode
    var tableModeHandler:any = undefined; 
    if(data.facets.length > 1) {
      tableModeHandler = setMode("table")
    } else {
      tableModeClass += " " + classes.inactiveModeControl
    }

    switch(viewMode) {
      case "table":
        cardContent = (
          <EnhancedTable rows={data.facets} />
        )

        break;


      default:
        cardContent = (
          <div className={summaryAreaClass}>
              <div  onClick={summaryAreaHandler}>
                {currentFacet.label && currentFacet.label.length > 0 &&
                <div className={classes.facetTitle}>
                  {currentFacet.label}
                </div>
                }
                <div className={classes.facetValue}>
                  <Field
                    key={currentFacet.type}
                    suppressTitle={true}
                    value={currentFacet.value}
                    data={secObj}
                    fieldDefinition={localField}
                  />
                </div>
              </div>
              {controls}
          </div>
        )
    }
  }

  return (
    <Card variant="outlined" className={classes.card}>
        <div className={classes.cardHeader}>
          <Typography className="cardHeaderTitle" variant="h6" color="inherit" >
            {
            // Only show the label if we have more than one facet, otherwise we will rely on the facet label alone.
            !suppressTitle && data.label}
          </Typography>
          {data.facets.length > 1 &&
            <div>
              <ButtonGroup className={classes.cardHeaderControls} variant="text" aria-label="text button group">
                <Button className={classes.cardHeaderButton}>
                  <i className={cardModeClass} onClick={setMode("card")} />
                </Button>
                <Button className={classes.cardHeaderButton}>
                  <i className={tableModeClass} onClick={tableModeHandler} />
                </Button>
              </ButtonGroup>

            </div>
          }
        </div>
        <div className={classes.cardBody}>
          <CardContent className={classes.cardContent}>
            { cardContent }
          </CardContent>
        </div>
    </Card>
  );
};
